import { useState, useEffect } from 'react'
import { api } from '@/api/client'
import { Button } from '@/components/ui/button'
import {
  LayoutGrid, User, Settings2, MessageSquare, BarChart3, Building2,
  Loader2, ChevronRight, ExternalLink, Mail, Phone, Globe, Clock,
  MapPin, ThumbsUp, MessageCircle, Share2, Send, Shield,
  CheckCircle2, XCircle, PenSquare, Link2, Plus,
  MessageSquarePlus, FileText, TrendingUp, Video, AtSign,
  Megaphone, Search, Users, Trash2, Code, Instagram,
  DollarSign, Zap, Play, Image,
} from 'lucide-react'
import { isReviewMode, isForexReview } from '@/lib/staging'
import { useAuth } from '@/api/hooks/useAuth'

// ── Types ──

interface PageInfo {
  page_id: string
  name: string
  category: string | null
  fan_count: number
  picture: string | null
  app_keys?: string[]
}

type Tab = 'pages' | 'profile' | 'metadata' | 'posts' | 'publish' | 'conversations' | 'business'
  | 'engagement' | 'user_content' | 'insights' | 'live' | 'email_tab' | 'branded' | 'creators' | 'asset_profile'
  | 'instagram' | 'ads' | 'marketing'

const _forex = window.location.hostname.includes('forexbrokereview')

const TABS: { key: Tab; label: string; icon: typeof LayoutGrid; permission: string }[] = [
  { key: 'pages', label: _forex ? 'Pages' : 'Páginas', icon: LayoutGrid, permission: 'pages_show_list' },
  { key: 'profile', label: _forex ? 'Profile' : 'Perfil', icon: User, permission: 'public_profile' },
  { key: 'metadata', label: _forex ? 'Metadata' : 'Metadados', icon: Settings2, permission: 'pages_manage_metadata' },
  { key: 'posts', label: 'Posts', icon: BarChart3, permission: 'pages_read_engagement' },
  { key: 'publish', label: _forex ? 'Publish' : 'Publicar', icon: PenSquare, permission: 'pages_manage_posts' },
  { key: 'engagement', label: _forex ? 'Comments' : 'Comentários', icon: MessageSquarePlus, permission: 'pages_manage_engagement' },
  { key: 'user_content', label: _forex ? 'Visitors' : 'Visitantes', icon: FileText, permission: 'pages_read_user_content' },
  { key: 'insights', label: 'Insights', icon: TrendingUp, permission: 'read_insights' },
  { key: 'conversations', label: _forex ? 'Messages' : 'Mensagens', icon: MessageSquare, permission: 'pages_messaging + pages_utility_messaging' },
  { key: 'live', label: _forex ? 'Live Video' : 'Lives', icon: Video, permission: 'Live Video API' },
  { key: 'email_tab', label: 'Email', icon: AtSign, permission: 'email' },
  { key: 'branded', label: 'Branded', icon: Megaphone, permission: 'facebook_branded_content_ads_brand' },
  { key: 'creators', label: _forex ? 'Creators' : 'Criadores', icon: Search, permission: 'facebook_creator_marketplace_discovery' },
  { key: 'instagram', label: 'Instagram', icon: Instagram, permission: 'instagram_basic + instagram_manage_messages' },
  { key: 'ads', label: _forex ? 'Ads' : 'Anúncios', icon: DollarSign, permission: 'ads_management' },
  { key: 'marketing', label: 'Marketing', icon: Zap, permission: 'marketing_messages_messenger + paid_marketing_messages' },
  { key: 'asset_profile', label: _forex ? 'Assets' : 'Ativos', icon: Users, permission: 'Business Asset User Profile Access' },
  { key: 'business', label: _forex ? 'Business' : 'Negócio', icon: Building2, permission: 'business_management' },
]

// ── Main Component ──

export function PageManager() {
  const [tab, setTab] = useState<Tab>('pages')
  const [pages, setPages] = useState<PageInfo[]>([])
  const [selectedPage, setSelectedPage] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const reviewMode = isReviewMode()
  const { data: user } = useAuth()

  const connectPage = () => {
    const token = localStorage.getItem('auth_token') || ''
    const isExpressDomain = window.location.hostname === 'app.blissf.com' || window.location.hostname.endsWith('conectagran.com.br') || window.location.hostname.endsWith('blissf.com')
    if (isExpressDomain) {
      window.location.href = `/auth?token=${encodeURIComponent(token)}&returnTo=${encodeURIComponent('/gerenciador')}`
    } else {
      // Laravel OAuth direct (no Express dependency)
      window.location.href = `/oauth/redirect?userId=${encodeURIComponent(user?.id || '')}&returnTo=${encodeURIComponent('/gerenciador')}`
    }
  }

  // Conectar a página no app dedicado à POSTAGEM (agendamentos). Sempre via Laravel
  // (/oauth/* → Laravel), independente do domínio — postagem é isolada do broadcast.
  const connectPosting = () => {
    window.location.href = `/oauth/redirect?userId=${encodeURIComponent(user?.id || '')}&returnTo=${encodeURIComponent('/gerenciador')}&app=posting`
  }

  // Conectar a página no app dedicado a CARD DE CAPTURA + SEQUENCIA (isolado do
  // broadcast). Sempre via Laravel (/oauth/* → Laravel).
  const connectCardApp = () => {
    window.location.href = `/oauth/redirect?userId=${encodeURIComponent(user?.id || '')}&returnTo=${encodeURIComponent('/gerenciador')}&app=card`
  }

  useEffect(() => {
    api.get<{ pages: PageInfo[] }>('/page-manager/pages')
      .then(d => {
        setPages(d.pages)
        if (d.pages.length > 0) setSelectedPage(d.pages[0].page_id)
      })
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
  }

  const currentPage = pages.find(p => p.page_id === selectedPage)

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{isForexReview() ? 'Page Manager' : 'Gerenciador de Páginas'}</h1>
          <p className="text-zinc-400 text-sm mt-1">{isForexReview() ? 'Manage your Facebook Pages — each tab demonstrates a specific Meta Graph API permission with full debug output.' : 'Gerencie suas páginas do Facebook — cada seção utiliza uma permissão específica da Meta Graph API.'}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button onClick={connectPage} size="sm" className="gap-2">
            <Plus className="w-4 h-4" /> {isForexReview() ? 'Connect Page' : 'Conectar Página'}
          </Button>
          {!isForexReview() && (
            <Button onClick={connectPosting} size="sm" variant="outline" className="gap-2" title="Conecta a página no app dedicado de postagem (isolado do broadcast)">
              <Plus className="w-4 h-4" /> Conectar postagem
            </Button>
          )}
          {!isForexReview() && (
            <Button onClick={connectCardApp} size="sm" variant="outline" className="gap-2" title="Conecta a página no app dedicado de card de captura + sequência (isolado do broadcast)">
              <Plus className="w-4 h-4" /> Conectar card
            </Button>
          )}
        </div>
      </div>

      {/* No pages — prompt to connect */}
      {pages.length === 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center space-y-4">
          <LayoutGrid className="w-12 h-12 text-zinc-600 mx-auto" />
          <h2 className="text-white font-semibold text-lg">{isForexReview() ? 'No pages connected' : 'Nenhuma página conectada'}</h2>
          <p className="text-zinc-400 text-sm max-w-md mx-auto">{isForexReview() ? 'Connect a Facebook Page to demonstrate Graph API permissions with real-time debug output.' : 'Conecte uma Página do Facebook para demonstrar as permissões da Graph API.'}</p>
          <Button onClick={connectPage} size="lg" className="gap-2">
            <Plus className="w-5 h-5" /> {isForexReview() ? 'Connect Facebook Page' : 'Conectar Página do Facebook'}
          </Button>
        </div>
      )}

      {/* Page selector (when not on "pages" tab) */}
      {tab !== 'pages' && pages.length > 1 && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-zinc-500">{isForexReview() ? 'Page:' : 'Página:'}</span>
          <select
            value={selectedPage || ''}
            onChange={e => setSelectedPage(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-md px-3 py-1.5 text-white text-sm"
          >
            {pages.map(p => <option key={p.page_id} value={p.page_id}>{p.name}</option>)}
          </select>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              tab === t.key
                ? 'bg-white/10 text-white'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Permission badge */}
      <PermissionBadge permission={TABS.find(t => t.key === tab)!.permission} />

      {/* Tab content */}
      {tab === 'pages' && <PagesTab pages={pages} onSelect={id => { setSelectedPage(id); setTab('profile') }} />}
      {tab === 'profile' && selectedPage && <ProfileTab pageId={selectedPage} />}
      {tab === 'metadata' && selectedPage && <MetadataTab pageId={selectedPage} />}
      {tab === 'posts' && selectedPage && <PostsTab pageId={selectedPage} />}
      {tab === 'publish' && selectedPage && <PublishTab pageId={selectedPage} />}
      {tab === 'engagement' && selectedPage && <EngagementTab pageId={selectedPage} />}
      {tab === 'user_content' && selectedPage && <UserContentTab pageId={selectedPage} />}
      {tab === 'insights' && selectedPage && <InsightsTab pageId={selectedPage} />}
      {tab === 'conversations' && selectedPage && <ConversationsTab pageId={selectedPage} />}
      {tab === 'live' && selectedPage && <LiveTab pageId={selectedPage} />}
      {tab === 'email_tab' && <EmailTab />}
      {tab === 'branded' && selectedPage && <BrandedContentTab pageId={selectedPage} />}
      {tab === 'creators' && selectedPage && <CreatorDiscoveryTab pageId={selectedPage} />}
      {tab === 'instagram' && selectedPage && <InstagramTab pageId={selectedPage} />}
      {tab === 'ads' && selectedPage && <AdsTab pageId={selectedPage} />}
      {tab === 'marketing' && selectedPage && <MarketingTab pageId={selectedPage} />}
      {tab === 'asset_profile' && selectedPage && <AssetProfileTab pageId={selectedPage} />}
      {tab === 'business' && selectedPage && <BusinessTab pageId={selectedPage} pageName={currentPage?.name || ''} />}
    </div>
  )
}

// ── Permission Badge ──

function PermissionBadge({ permission }: { permission: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
      <Shield className="w-4 h-4 text-blue-400 shrink-0" />
      <p className="text-xs text-blue-300">
        <span className="text-blue-400 font-semibold">Permissão utilizada:</span>{' '}
        <code className="bg-blue-500/20 px-1.5 py-0.5 rounded">{permission}</code>
        {' — '}dados obtidos em tempo real da Meta Graph API via <code className="bg-blue-500/20 px-1.5 py-0.5 rounded">{window.location.hostname} → graph.facebook.com</code>
      </p>
    </div>
  )
}

// ── Tab: Minhas Páginas (pages_show_list) ──

function PagesTab({ pages, onSelect }: { pages: PageInfo[]; onSelect: (id: string) => void }) {
  return (
    <div className="space-y-3">
      <p className="text-zinc-400 text-sm">
        Listando <strong className="text-white">{pages.length}</strong> páginas conectadas à sua conta.
        Clique em uma página para gerenciá-la.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {pages.map(page => (
          <button
            key={page.page_id}
            onClick={() => onSelect(page.page_id)}
            className="flex items-center gap-3 p-4 bg-zinc-900 border border-zinc-800 rounded-xl hover:border-zinc-600 transition-colors text-left group"
          >
            {page.picture
              ? <img src={page.picture} className="w-12 h-12 rounded-full object-cover shrink-0" />
              : <div className="w-12 h-12 rounded-full bg-zinc-700 flex items-center justify-center shrink-0 text-lg font-bold text-zinc-400">{page.name[0]}</div>
            }
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm truncate">{page.name}</p>
              {page.category && <p className="text-zinc-500 text-xs truncate">{page.category}</p>}
              <p className="text-zinc-500 text-xs">{page.fan_count.toLocaleString()} curtidas</p>
              {page.app_keys && page.app_keys.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {page.app_keys.includes('broadcast') && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/15 text-blue-300">broadcast</span>
                  )}
                  {page.app_keys.includes('posting') && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-300">postagem</span>
                  )}
                </div>
              )}
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 shrink-0" />
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Tab: Perfil (public_profile) ──

function ProfileTab({ pageId }: { pageId: string }) {
  const [data, setData] = useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.get<{ profile: Record<string, unknown> }>(`/page-manager/${pageId}/profile`)
      .then(d => setData(d.profile))
      .finally(() => setLoading(false))
  }, [pageId])

  if (loading) return <Spinner />
  if (!data) return <Empty />

  const cover = (data.cover as { source?: string })?.source
  const picture = (data.picture as { data?: { url?: string } })?.data?.url

  return (
    <div className="space-y-4">
      {/* Cover + avatar */}
      <div className="relative rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800">
        {cover && <img src={cover} className="w-full h-48 object-cover" />}
        <div className="flex items-end gap-4 p-4 -mt-8 relative z-10">
          {picture
            ? <img src={picture} className="w-20 h-20 rounded-xl border-4 border-zinc-900 object-cover" />
            : <div className="w-20 h-20 rounded-xl border-4 border-zinc-900 bg-zinc-700 flex items-center justify-center text-2xl font-bold text-zinc-400">{(data.name as string)?.[0]}</div>
          }
          <div>
            <h2 className="text-xl font-bold text-white">{data.name as string}</h2>
            <p className="text-zinc-400 text-sm">{data.category as string}</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Curtidas" value={data.fan_count as number} />
        <StatCard label="Seguidores" value={data.followers_count as number} />
        <StatCard label="Falando sobre" value={data.talking_about_count as number} />
      </div>

      {data.link && (
        <a href={data.link as string} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300">
          <ExternalLink className="w-4 h-4" /> Ver no Facebook
        </a>
      )}
    </div>
  )
}

// ── Tab: Metadados (pages_manage_metadata) ──

function MetadataTab({ pageId }: { pageId: string }) {
  const [data, setData] = useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveResult, setSaveResult] = useState<{ success: boolean; response: unknown } | null>(null)
  const [form, setForm] = useState({ about: '', description: '', phone: '', website: '' })
  const forex = _forex

  useEffect(() => {
    setLoading(true)
    setSaveResult(null)
    api.get<{ metadata: Record<string, unknown> }>(`/page-manager/${pageId}/metadata`)
      .then(d => {
        setData(d.metadata)
        setForm({
          about: (d.metadata.about as string) || '',
          description: (d.metadata.description as string) || '',
          phone: (d.metadata.phone as string) || '',
          website: (d.metadata.website as string) || '',
        })
      })
      .finally(() => setLoading(false))
  }, [pageId])

  const handleSave = async () => {
    setSaving(true)
    setSaveResult(null)
    try {
      const res = await api.post<{ success: boolean; response: unknown }>(`/page-manager/${pageId}/metadata`, form)
      setSaveResult(res)
      if (res.success) {
        setEditing(false)
        // Reload data
        const d = await api.get<{ metadata: Record<string, unknown> }>(`/page-manager/${pageId}/metadata`)
        setData(d.metadata)
      }
    } catch (e) {
      setSaveResult({ success: false, response: e instanceof Error ? e.message : 'Error' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Spinner />
  if (!data) return <Empty />

  const location = data.location as Record<string, string> | undefined

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-zinc-400 text-sm">{forex ? 'Page metadata via' : 'Metadados da página obtidos via'} <code className="text-zinc-300">pages_manage_metadata</code>.</p>
        <Button onClick={() => { setEditing(!editing); setSaveResult(null) }} size="sm" variant={editing ? 'destructive' : 'outline'} className="gap-2">
          <PenSquare className="w-3.5 h-3.5" />
          {editing ? (forex ? 'Cancel' : 'Cancelar') : (forex ? 'Edit Metadata' : 'Editar Metadados')}
        </Button>
      </div>

      {editing ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-4">
          <h3 className="text-white font-semibold text-sm">{forex ? 'Edit Page Metadata' : 'Editar Metadados'}</h3>
          {(['about', 'description', 'phone', 'website'] as const).map(field => (
            <div key={field}>
              <label className="text-zinc-500 text-xs font-medium block mb-1.5 capitalize">{field}</label>
              {field === 'about' || field === 'description' ? (
                <textarea
                  value={form[field]}
                  onChange={e => setForm(prev => ({ ...prev, [field]: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm resize-none h-20 placeholder:text-zinc-600"
                  placeholder={`Page ${field}...`}
                />
              ) : (
                <input
                  type="text"
                  value={form[field]}
                  onChange={e => setForm(prev => ({ ...prev, [field]: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm placeholder:text-zinc-600"
                  placeholder={field === 'phone' ? '+1 (555) 000-0000' : 'https://...'}
                />
              )}
            </div>
          ))}
          <Button onClick={handleSave} disabled={saving} size="sm" className="gap-2">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {saving ? (forex ? 'Saving...' : 'Salvando...') : (forex ? 'Save Changes' : 'Salvar')}
          </Button>
          <p className="text-zinc-600 text-xs">API: POST /{pageId} — {forex ? 'Updates page metadata via' : 'Atualiza metadados via'} <code>pages_manage_metadata</code></p>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl divide-y divide-zinc-800">
          <MetaRow label={forex ? 'Name' : 'Nome'} value={data.name as string} />
          <MetaRow label={forex ? 'Category' : 'Categoria'} value={data.category as string} />
          <MetaRow label={forex ? 'About' : 'Sobre'} value={data.about as string} />
          <MetaRow label={forex ? 'Description' : 'Descrição'} value={data.description as string} />
          <MetaRow label="Website" value={data.website as string} icon={<Globe className="w-4 h-4" />} />
          <MetaRow label="Email" value={(data.emails as string[])?.join(', ')} icon={<Mail className="w-4 h-4" />} />
          <MetaRow label={forex ? 'Phone' : 'Telefone'} value={data.phone as string} icon={<Phone className="w-4 h-4" />} />
          <MetaRow label={forex ? 'Address' : 'Endereço'} value={data.single_line_address as string || (location ? `${location.city}, ${location.state}, ${location.country}` : undefined)} icon={<MapPin className="w-4 h-4" />} />
          {data.hours && (
            <div className="p-4">
              <div className="flex items-center gap-2 text-zinc-500 text-sm mb-2">
                <Clock className="w-4 h-4" />
                <span className="font-medium">{forex ? 'Hours' : 'Horários'}</span>
              </div>
              <pre className="text-xs text-zinc-400 bg-zinc-950 rounded p-3 overflow-x-auto">
                {JSON.stringify(data.hours, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {saveResult && (
        <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${saveResult.success ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-300' : 'bg-red-500/10 border border-red-500/20 text-red-300'}`}>
          {saveResult.success ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
          <div>
            <p className="font-medium">{saveResult.success ? (forex ? 'Metadata updated successfully!' : 'Metadados atualizados!') : (forex ? 'Failed to update' : 'Erro ao atualizar')}</p>
            <p className="text-xs mt-1 opacity-70">Permission: <code>pages_manage_metadata</code></p>
          </div>
        </div>
      )}
      {saveResult && <DebugPanel title="Update API Response" data={saveResult} />}
    </div>
  )
}

function MetaRow({ label, value, icon }: { label: string; value?: string | null; icon?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 p-4">
      <div className="flex items-center gap-2 w-32 shrink-0">
        {icon && <span className="text-zinc-500">{icon}</span>}
        <span className="text-zinc-500 text-sm font-medium">{label}</span>
      </div>
      <span className="text-zinc-200 text-sm">{value || <span className="text-zinc-600 italic">{_forex ? 'Not set' : 'Não definido'}</span>}</span>
    </div>
  )
}

// ── Tab: Posts (pages_read_engagement) ──

function PostsTab({ pageId }: { pageId: string }) {
  const [posts, setPosts] = useState<Record<string, unknown>[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.get<{ posts: Record<string, unknown>[] }>(`/page-manager/${pageId}/posts`)
      .then(d => setPosts(d.posts))
      .finally(() => setLoading(false))
  }, [pageId])

  if (loading) return <Spinner />

  return (
    <div className="space-y-4">
      <p className="text-zinc-400 text-sm">
        <strong className="text-white">{posts.length}</strong> posts publicados com métricas de engajamento.
      </p>

      <div className="space-y-3">
        {posts.map((post, i) => {
          const likes = (post.likes as { summary?: { total_count?: number } })?.summary?.total_count || 0
          const comments = (post.comments as { summary?: { total_count?: number } })?.summary?.total_count || 0
          const shares = (post.shares as { count?: number })?.count || 0
          const date = post.created_time ? new Date(post.created_time as string).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''

          return (
            <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              {post.full_picture && (
                <img src={post.full_picture as string} className="w-full h-48 object-cover" />
              )}
              <div className="p-4 space-y-2">
                <p className="text-zinc-500 text-xs">{date}</p>
                {post.message && <p className="text-zinc-200 text-sm line-clamp-3">{post.message as string}</p>}
                <div className="flex items-center gap-4 pt-2 border-t border-zinc-800">
                  <span className="flex items-center gap-1.5 text-zinc-400 text-xs">
                    <ThumbsUp className="w-3.5 h-3.5" /> {likes.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1.5 text-zinc-400 text-xs">
                    <MessageCircle className="w-3.5 h-3.5" /> {comments.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1.5 text-zinc-400 text-xs">
                    <Share2 className="w-3.5 h-3.5" /> {shares.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
        {posts.length === 0 && <Empty message="Nenhum post publicado nesta página." />}
      </div>
    </div>
  )
}

// ── Tab: Publicar (pages_manage_posts) ──

function PublishTab({ pageId }: { pageId: string }) {
  const [message, setMessage] = useState('')
  const [link, setLink] = useState('')
  const [publishing, setPublishing] = useState(false)
  const [result, setResult] = useState<{ success: boolean; postId?: string; error?: string } | null>(null)

  const publish = async () => {
    if (!message.trim()) return
    setPublishing(true)
    setResult(null)
    try {
      const res = await api.post<{ success: boolean; post_id?: string; response: Record<string, unknown> }>(`/page-manager/${pageId}/publish`, {
        message: message.trim(),
        link: link.trim() || undefined,
      })
      if (res.success) {
        setResult({ success: true, postId: res.post_id })
        setMessage('')
        setLink('')
      } else {
        setResult({ success: false, error: JSON.stringify(res.response) })
      }
    } catch (e) {
      setResult({ success: false, error: e instanceof Error ? e.message : 'Erro ao publicar' })
    } finally {
      setPublishing(false)
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-zinc-400 text-sm">
        Publique um post na página utilizando a permissão <code className="text-zinc-300">pages_manage_posts</code>.
        O post será publicado imediatamente via <code className="text-zinc-300">POST /{'{page_id}'}/feed</code>.
      </p>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-4">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2">
          <PenSquare className="w-4 h-4" /> Criar Publicação
        </h3>

        <div>
          <label className="text-zinc-500 text-xs font-medium block mb-1.5">Texto do post</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Escreva o conteúdo do post..."
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm resize-none h-32 placeholder:text-zinc-600"
            maxLength={5000}
          />
          <p className="text-zinc-600 text-xs text-right mt-1">{message.length}/5000</p>
        </div>

        <div>
          <label className="text-zinc-500 text-xs font-medium block mb-1.5 flex items-center gap-1.5">
            <Link2 className="w-3.5 h-3.5" /> Link (opcional)
          </label>
          <input
            type="url"
            value={link}
            onChange={e => setLink(e.target.value)}
            placeholder="https://exemplo.com"
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm placeholder:text-zinc-600"
          />
        </div>

        <Button
          onClick={publish}
          disabled={!message.trim() || publishing}
          size="sm"
          className="gap-2"
        >
          {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Publicar (pages_manage_posts)
        </Button>

        {result && (
          <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
            result.success ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-300' : 'bg-red-500/10 border border-red-500/20 text-red-300'
          }`}>
            {result.success ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
            <div>
              {result.success ? (
                <>
                  <p className="font-medium">Post publicado com sucesso!</p>
                  <p className="text-xs mt-1 opacity-70">Post ID: <code>{result.postId}</code></p>
                  <p className="text-xs mt-0.5 opacity-70">API: <code>POST /{pageId}/feed</code> | Permissão: <code>pages_manage_posts</code></p>
                </>
              ) : (
                <>
                  <p className="font-medium">Erro ao publicar</p>
                  <p className="text-xs mt-1 opacity-70">{result.error}</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Tab: Mensagens (pages_messaging + pages_utility_messaging) ──

function ConversationsTab({ pageId }: { pageId: string }) {
  const [conversations, setConversations] = useState<Record<string, unknown>[]>([])
  const [features, setFeatures] = useState<Record<string, unknown>[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedRecipient, setSelectedRecipient] = useState<{ id: string; name: string } | null>(null)
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [sendResult, setSendResult] = useState<{ type: string; success: boolean; text: string } | null>(null)

  useEffect(() => {
    setLoading(true)
    setSendResult(null)
    setSelectedRecipient(null)
    Promise.all([
      api.get<{ conversations: Record<string, unknown>[] }>(`/page-manager/${pageId}/conversations`),
      api.get<{ features: Record<string, unknown>[] }>(`/page-manager/${pageId}/messaging-status`),
    ]).then(([convData, featData]) => {
      setConversations(convData.conversations)
      setFeatures(featData.features)
    }).finally(() => setLoading(false))
  }, [pageId])

  const sendMsg = async (type: 'standard' | 'utility') => {
    if (!selectedRecipient || !message.trim()) return
    setSending(true)
    setSendResult(null)
    try {
      const endpoint = type === 'utility' ? 'send-utility-message' : 'send-message'
      const res = await api.post<{ success: boolean; response: Record<string, unknown> }>(`/page-manager/${pageId}/${endpoint}`, {
        recipient_id: selectedRecipient.id,
        message: message.trim(),
      })
      setSendResult({
        type: type === 'utility' ? 'pages_utility_messaging' : 'pages_messaging',
        success: res.success,
        text: res.success ? 'Mensagem enviada com sucesso!' : `Erro: ${JSON.stringify(res.response)}`,
      })
      if (res.success) setMessage('')
    } catch (e) {
      setSendResult({ type, success: false, text: e instanceof Error ? e.message : 'Erro ao enviar' })
    } finally {
      setSending(false)
    }
  }

  if (loading) return <Spinner />

  return (
    <div className="space-y-6">
      {/* Send message section */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-4">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2">
          <Send className="w-4 h-4" /> Enviar Mensagem
        </h3>

        {/* Recipient selector */}
        <div>
          <label className="text-zinc-500 text-xs font-medium block mb-1.5">Destinatário (selecione uma conversa abaixo)</label>
          {selectedRecipient ? (
            <div className="flex items-center gap-2 px-3 py-2 bg-zinc-800 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-zinc-600 flex items-center justify-center text-xs font-bold text-zinc-300">{selectedRecipient.name[0]}</div>
              <span className="text-white text-sm">{selectedRecipient.name}</span>
              <span className="text-zinc-600 text-xs">({selectedRecipient.id})</span>
              <button onClick={() => setSelectedRecipient(null)} className="ml-auto text-zinc-500 hover:text-zinc-300 text-xs">Trocar</button>
            </div>
          ) : (
            <p className="text-amber-400 text-xs">Clique em uma conversa abaixo para selecionar o destinatário.</p>
          )}
        </div>

        {/* Message input */}
        <div>
          <label className="text-zinc-500 text-xs font-medium block mb-1.5">Mensagem</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Digite a mensagem de teste..."
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm resize-none h-20 placeholder:text-zinc-600"
          />
        </div>

        {/* Send buttons */}
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => sendMsg('standard')}
            disabled={!selectedRecipient || !message.trim() || sending}
            size="sm"
            className="gap-2"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
            Enviar (pages_messaging)
          </Button>
          <Button
            onClick={() => sendMsg('utility')}
            disabled={!selectedRecipient || !message.trim() || sending}
            size="sm"
            variant="outline"
            className="gap-2"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Enviar Utilidade (pages_utility_messaging)
          </Button>
        </div>

        {/* Send result */}
        {sendResult && (
          <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
            sendResult.success ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-300' : 'bg-red-500/10 border border-red-500/20 text-red-300'
          }`}>
            {sendResult.success ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
            <div>
              <p className="font-medium">{sendResult.text}</p>
              <p className="text-xs mt-1 opacity-70">Permissão: <code>{sendResult.type}</code></p>
            </div>
          </div>
        )}

        {/* Demographics test */}
        {selectedRecipient && (
          <DemographicsTest pageId={pageId} psid={selectedRecipient.id} />
        )}
      </div>

      {/* Utility messaging status */}
      <div>
        <PermissionBadge permission="pages_utility_messaging" />
        <div className="mt-3 bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" /> Status de Messaging Features
          </h3>
          {features.length > 0 ? (
            <div className="space-y-2">
              {features.map((f, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-300">{f.feature as string}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    f.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' :
                    f.status === 'pending' ? 'bg-amber-500/20 text-amber-300' :
                    'bg-zinc-700 text-zinc-400'
                  }`}>
                    {f.status as string}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-sm">Nenhuma feature de messaging encontrada.</p>
          )}
        </div>
      </div>

      {/* Conversations list */}
      <div>
        <PermissionBadge permission="pages_messaging" />
        <div className="mt-3 space-y-2">
          <p className="text-zinc-400 text-sm">
            <strong className="text-white">{conversations.length}</strong> conversas recentes — clique para selecionar como destinatário.
          </p>
          {conversations.map((conv, i) => {
            const participants = (conv.participants as { data?: Array<{ name?: string; id?: string }> })?.data || []
            const other = participants.find(p => p.name)
            const isSelected = selectedRecipient?.id === other?.id
            const date = conv.updated_time ? new Date(conv.updated_time as string).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''

            return (
              <button
                key={i}
                onClick={() => other && setSelectedRecipient({ id: other.id!, name: other.name! })}
                className={`flex items-center gap-3 p-3 w-full text-left rounded-lg border transition-colors ${
                  isSelected
                    ? 'bg-blue-500/10 border-blue-500/30'
                    : 'bg-zinc-900 border-zinc-800 hover:border-zinc-600'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-sm font-bold ${
                  isSelected ? 'bg-blue-500/20 text-blue-300' : 'bg-zinc-700 text-zinc-400'
                }`}>
                  {other?.name?.[0] || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{other?.name || 'Participante'}</p>
                  {conv.snippet && <p className="text-zinc-500 text-xs truncate">{conv.snippet as string}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-zinc-500 text-xs">{date}</p>
                  {conv.message_count && <p className="text-zinc-600 text-xs">{conv.message_count as number} msgs</p>}
                </div>
              </button>
            )
          })}
          {conversations.length === 0 && <Empty message="Nenhuma conversa encontrada." />}
        </div>
      </div>
    </div>
  )
}

// ── Tab: Negócio (business_management) ──

function BusinessTab({ pageId, pageName }: { pageId: string; pageName: string }) {
  const [data, setData] = useState<{ business: Record<string, unknown> | null } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.get<{ business: Record<string, unknown> | null; page_name: string }>(`/page-manager/${pageId}/business`)
      .then(d => setData(d))
      .finally(() => setLoading(false))
  }, [pageId])

  if (loading) return <Spinner />

  const biz = data?.business

  return (
    <div className="space-y-4">
      <p className="text-zinc-400 text-sm">
        Informações do Business Manager vinculado à página <strong className="text-white">{pageName}</strong>.
      </p>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        {biz ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-blue-400" />
              <div>
                <h3 className="text-white font-bold text-lg">{biz.name as string}</h3>
                <p className="text-zinc-500 text-sm">ID: {biz.id as string}</p>
              </div>
            </div>
            {biz.verification_status && (
              <div className="flex items-center gap-2">
                <span className="text-zinc-500 text-sm">Verificação:</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  biz.verification_status === 'verified' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'
                }`}>
                  {biz.verification_status as string}
                </span>
              </div>
            )}
            {biz.link && (
              <a href={biz.link as string} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300">
                <ExternalLink className="w-4 h-4" /> Abrir no Business Manager
              </a>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <Building2 className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">Nenhum Business Manager vinculado a esta página.</p>
            <p className="text-zinc-600 text-sm mt-1">A página pode ser gerenciada diretamente sem portfólio empresarial.</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Debug Panel (reusable) ──

function DebugPanel({ title, data }: { title: string; data: unknown }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="mt-4 border border-zinc-800 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 w-full px-3 py-2 bg-zinc-900 hover:bg-zinc-800 text-left text-xs">
        <Code className="w-3.5 h-3.5 text-amber-400" />
        <span className="text-amber-400 font-medium">{title}</span>
        <span className="text-zinc-600 ml-auto">{open ? '▼' : '▶'}</span>
      </button>
      {open && (
        <pre className="p-3 bg-zinc-950 text-xs text-zinc-400 overflow-x-auto max-h-80 overflow-y-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  )
}

// ── Tab: Comentários (pages_manage_engagement) ──

function EngagementTab({ pageId }: { pageId: string }) {
  const [comments, setComments] = useState<Record<string, unknown>[]>([])
  const [loading, setLoading] = useState(true)
  const [rawResponse, setRawResponse] = useState<unknown>(null)
  const [newComment, setNewComment] = useState('')
  const [selectedPostId, setSelectedPostId] = useState('')
  const [actionResult, setActionResult] = useState<{ success: boolean; text: string } | null>(null)

  const load = () => {
    setLoading(true)
    api.get<{ comments: Record<string, unknown>[]; api_call: string }>(`/page-manager/${pageId}/comments`)
      .then(d => { setComments(d.comments); setRawResponse(d) })
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [pageId])

  const postComment = async () => {
    if (!selectedPostId || !newComment.trim()) return
    setActionResult(null)
    try {
      const res = await api.post<{ success: boolean; response: unknown }>(`/page-manager/${pageId}/comments`, {
        post_id: selectedPostId, message: newComment.trim(),
      })
      setActionResult({ success: res.success, text: res.success ? 'Comentário criado!' : JSON.stringify(res.response) })
      if (res.success) { setNewComment(''); load() }
    } catch (e) { setActionResult({ success: false, text: (e as Error).message }) }
  }

  const deleteComment = async (commentId: string) => {
    try {
      const res = await api.delete<{ success: boolean; response: unknown }>(`/page-manager/${pageId}/comments/${commentId}`)
      setActionResult({ success: res.success, text: res.success ? 'Comentário deletado!' : JSON.stringify(res.response) })
      if (res.success) load()
    } catch (e) { setActionResult({ success: false, text: (e as Error).message }) }
  }

  if (loading) return <Spinner />

  const postIds = [...new Set(comments.map(c => c.post_id as string))]

  return (
    <div className="space-y-4">
      <p className="text-zinc-400 text-sm">Gerenciar comentários — criar, listar e deletar via <code className="text-zinc-300">pages_manage_engagement</code>.</p>

      {/* Create comment */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2"><MessageSquarePlus className="w-4 h-4" /> Criar Comentário</h3>
        <select value={selectedPostId} onChange={e => setSelectedPostId(e.target.value)}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
          <option value="">Selecione um post...</option>
          {postIds.map(pid => <option key={pid} value={pid}>{pid}</option>)}
        </select>
        <textarea value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Texto do comentário..."
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm resize-none h-20 placeholder:text-zinc-600" />
        <Button onClick={postComment} disabled={!selectedPostId || !newComment.trim()} size="sm" className="gap-2">
          <Send className="w-3.5 h-3.5" /> Comentar (pages_manage_engagement)
        </Button>
        {actionResult && (
          <div className={`p-3 rounded-lg text-sm ${actionResult.success ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'}`}>
            {actionResult.text}
          </div>
        )}
      </div>

      {/* Comment list */}
      <div className="space-y-2">
        <p className="text-zinc-500 text-xs"><strong className="text-white">{comments.length}</strong> comentários encontrados</p>
        {comments.map((c, i) => (
          <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-zinc-500 text-xs">Post: {(c.post_message as string) || c.post_id as string}</p>
              <p className="text-white text-sm mt-1">{c.message as string}</p>
              <p className="text-zinc-600 text-xs mt-1">De: {(c.from as { name?: string })?.name || '?'} • {c.like_count as number || 0} likes</p>
            </div>
            <button onClick={() => deleteComment(c.id as string)} className="text-red-500 hover:text-red-400 shrink-0" title="Deletar">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <DebugPanel title="Raw API Response" data={rawResponse} />
    </div>
  )
}

// ── Tab: Conteúdo de Visitantes (pages_read_user_content) ──

function UserContentTab({ pageId }: { pageId: string }) {
  const [data, setData] = useState<{ feed: Record<string, unknown>[]; ratings: Record<string, unknown>[]; feed_error?: unknown; ratings_error?: unknown } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.get<{ feed: Record<string, unknown>[]; ratings: Record<string, unknown>[]; feed_error?: unknown; ratings_error?: unknown }>(`/page-manager/${pageId}/user-content`)
      .then(d => setData(d))
      .finally(() => setLoading(false))
  }, [pageId])

  if (loading) return <Spinner />
  if (!data) return <Empty />

  return (
    <div className="space-y-6">
      <p className="text-zinc-400 text-sm">Conteúdo gerado por usuários — posts de visitantes e avaliações via <code className="text-zinc-300">pages_read_user_content</code>.</p>

      {data.feed_error && <DebugPanel title="Feed Error" data={data.feed_error} />}
      {data.ratings_error && <DebugPanel title="Ratings Error" data={data.ratings_error} />}

      {/* Feed */}
      <div>
        <h3 className="text-white font-semibold text-sm mb-3">Feed da Página ({data.feed.length} itens)</h3>
        <div className="space-y-2">
          {data.feed.map((item, i) => (
            <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
              <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1">
                <span className="font-medium text-zinc-300">{(item.from as { name?: string })?.name || 'Página'}</span>
                <span>•</span>
                <span>{item.created_time ? new Date(item.created_time as string).toLocaleDateString('pt-BR') : ''}</span>
                <span className="ml-auto px-1.5 py-0.5 bg-zinc-800 rounded text-zinc-500">{item.type as string || 'post'}</span>
              </div>
              {item.message && <p className="text-zinc-200 text-sm line-clamp-3">{item.message as string}</p>}
              {item.full_picture && <img src={item.full_picture as string} className="mt-2 rounded-lg max-h-40 object-cover" />}
            </div>
          ))}
          {data.feed.length === 0 && <Empty message="Nenhum item no feed." />}
        </div>
      </div>

      {/* Ratings */}
      <div>
        <h3 className="text-white font-semibold text-sm mb-3">Avaliações ({data.ratings.length})</h3>
        <div className="space-y-2">
          {data.ratings.map((r, i) => (
            <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <span className="text-amber-400 font-bold">{'★'.repeat(r.rating as number || 0)}</span>
                <span className="text-zinc-500 text-xs">{(r.reviewer as { name?: string })?.name}</span>
              </div>
              {r.review_text && <p className="text-zinc-300 text-sm mt-1">{r.review_text as string}</p>}
            </div>
          ))}
          {data.ratings.length === 0 && <Empty message="Nenhuma avaliação." />}
        </div>
      </div>

      <DebugPanel title="Raw API Response" data={data} />
    </div>
  )
}

// ── Tab: Insights (read_insights) ──

function InsightsTab({ pageId }: { pageId: string }) {
  const [data, setData] = useState<{ insights: Record<string, unknown>[]; error?: unknown } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.get<{ insights: Record<string, unknown>[]; error?: unknown; api_call: string }>(`/page-manager/${pageId}/insights`)
      .then(d => setData(d))
      .finally(() => setLoading(false))
  }, [pageId])

  if (loading) return <Spinner />
  if (!data) return <Empty />

  return (
    <div className="space-y-4">
      <p className="text-zinc-400 text-sm">Analytics da página (últimos 7 dias) via <code className="text-zinc-300">read_insights</code>.</p>

      {data.error && <DebugPanel title="API Error" data={data.error} />}

      <div className="space-y-3">
        {data.insights.map((metric, i) => {
          const values = (metric.values as { value: number; end_time: string }[]) || []
          const latest = values[values.length - 1]?.value || 0
          return (
            <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="text-white font-medium text-sm">{metric.title as string || metric.name as string}</h4>
                  <p className="text-zinc-600 text-xs">{metric.description as string}</p>
                </div>
                <p className="text-2xl font-bold text-white">{latest.toLocaleString()}</p>
              </div>
              <div className="flex gap-1 items-end h-12">
                {values.map((v, j) => {
                  const max = Math.max(...values.map(x => x.value), 1)
                  return (
                    <div key={j} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-blue-500/30 rounded-sm" style={{ height: `${(v.value / max) * 48}px`, minHeight: 2 }} />
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
        {data.insights.length === 0 && <Empty message="Nenhum dado de insights disponível." />}
      </div>

      <DebugPanel title="Raw API Response" data={data} />
    </div>
  )
}

// ── Tab: Lives (Live Video API) ──

function LiveTab({ pageId }: { pageId: string }) {
  const [videos, setVideos] = useState<Record<string, unknown>[]>([])
  const [loading, setLoading] = useState(true)
  const [rawResponse, setRawResponse] = useState<unknown>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [creating, setCreating] = useState(false)
  const [result, setResult] = useState<{ success: boolean; text: string; data?: unknown } | null>(null)

  const load = () => {
    setLoading(true)
    api.get<{ videos: Record<string, unknown>[]; error?: unknown }>(`/page-manager/${pageId}/live-videos`)
      .then(d => { setVideos(d.videos); setRawResponse(d) })
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [pageId])

  const createLive = async () => {
    if (!title.trim()) return
    setCreating(true)
    setResult(null)
    try {
      const res = await api.post<{ success: boolean; response: unknown }>(`/page-manager/${pageId}/live-videos`, {
        title: title.trim(), description: description.trim(),
      })
      setResult({ success: res.success, text: res.success ? 'Live video criado!' : 'Erro ao criar', data: res.response })
      if (res.success) { setTitle(''); setDescription(''); load() }
    } catch (e) { setResult({ success: false, text: (e as Error).message }) }
    finally { setCreating(false) }
  }

  if (loading) return <Spinner />

  return (
    <div className="space-y-4">
      <p className="text-zinc-400 text-sm">Gerenciar transmissões ao vivo via <code className="text-zinc-300">Live Video API</code> + <code className="text-zinc-300">pages_manage_posts</code>.</p>

      {/* Create live */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Video className="w-4 h-4 text-red-400" /> Criar Transmissão ao Vivo</h3>
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Título da live..."
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm placeholder:text-zinc-600" />
        <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Descrição (opcional)..."
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm resize-none h-20 placeholder:text-zinc-600" />
        <Button onClick={createLive} disabled={!title.trim() || creating} size="sm" className="gap-2">
          {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Video className="w-3.5 h-3.5" />}
          Criar Live (UNPUBLISHED)
        </Button>
        {result && (
          <div className={`p-3 rounded-lg text-sm ${result.success ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'}`}>
            <p>{result.text}</p>
            {result.data && <pre className="text-xs mt-2 opacity-70 overflow-x-auto">{JSON.stringify(result.data, null, 2)}</pre>}
          </div>
        )}
      </div>

      {/* Video list */}
      <div className="space-y-2">
        <h3 className="text-white font-semibold text-sm">Lives Existentes ({videos.length})</h3>
        {videos.map((v, i) => (
          <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-medium">{v.title as string || 'Sem título'}</p>
                <p className="text-zinc-500 text-xs mt-0.5">{v.description as string}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                v.status === 'LIVE_NOW' ? 'bg-red-500/20 text-red-300' :
                v.status === 'VOD' ? 'bg-emerald-500/20 text-emerald-300' :
                'bg-zinc-700 text-zinc-400'
              }`}>{v.status as string}</span>
            </div>
            <p className="text-zinc-600 text-xs mt-1">Views: {v.live_views as number || 0} • {v.creation_time ? new Date(v.creation_time as string).toLocaleDateString('pt-BR') : ''}</p>
          </div>
        ))}
        {videos.length === 0 && <Empty message="Nenhuma transmissão ao vivo." />}
      </div>

      <DebugPanel title="Raw API Response" data={rawResponse} />
    </div>
  )
}

// ── Tab: Email (email) ──

function EmailTab() {
  const [data, setData] = useState<{ user: Record<string, unknown>; error?: unknown } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.get<{ user: Record<string, unknown>; error?: unknown; api_call: string }>('/page-manager/user-email')
      .then(d => setData(d))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <Spinner />
  if (!data) return <Empty />

  return (
    <div className="space-y-4">
      <p className="text-zinc-400 text-sm">Email do usuário autenticado via <code className="text-zinc-300">email</code> permission + <code className="text-zinc-300">GET /me?fields=id,name,email</code>.</p>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-3">
        <div className="flex items-center gap-3">
          <AtSign className="w-8 h-8 text-blue-400" />
          <div>
            <h3 className="text-white font-bold text-lg">{data.user.name as string || 'N/A'}</h3>
            <p className="text-blue-400 text-sm">{data.user.email as string || 'Email não disponível'}</p>
            <p className="text-zinc-600 text-xs mt-0.5">FB ID: {data.user.id as string}</p>
          </div>
        </div>
      </div>

      {data.error && <DebugPanel title="API Error" data={data.error} />}
      <DebugPanel title="Raw API Response" data={data} />
    </div>
  )
}

// ── Tab: Branded Content (facebook_branded_content_ads_brand) ──

function BrandedContentTab({ pageId }: { pageId: string }) {
  const [data, setData] = useState<{ permissions: unknown; page_info: unknown; error?: unknown } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.get<{ permissions: unknown; page_info: unknown; error?: unknown }>(`/page-manager/${pageId}/branded-content`)
      .then(d => setData(d))
      .finally(() => setLoading(false))
  }, [pageId])

  if (loading) return <Spinner />
  if (!data) return <Empty />

  return (
    <div className="space-y-4">
      <p className="text-zinc-400 text-sm">Branded Content & Partnership Ads via <code className="text-zinc-300">facebook_branded_content_ads_brand</code>.</p>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2 mb-3"><Megaphone className="w-4 h-4 text-purple-400" /> Permissões de Branded Content</h3>
        <p className="text-zinc-400 text-sm">
          Esta permissão permite ler posts onde o perfil do usuário é tagueado como parceiro pago e gerenciar permissões de Partnership Ads.
        </p>
      </div>

      {data.error && <DebugPanel title="API Error" data={data.error} />}
      <DebugPanel title="Branded Content Permissions" data={data.permissions} />
      <DebugPanel title="Page Info" data={data.page_info} />
    </div>
  )
}

// ── Tab: Creator Discovery (facebook_creator_marketplace_discovery) ──

function CreatorDiscoveryTab({ pageId }: { pageId: string }) {
  const [data, setData] = useState<{ creator_info: unknown; error?: unknown } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.get<{ creator_info: unknown; error?: unknown }>(`/page-manager/${pageId}/creator-discovery`)
      .then(d => setData(d))
      .finally(() => setLoading(false))
  }, [pageId])

  if (loading) return <Spinner />
  if (!data) return <Empty />

  const info = data.creator_info as Record<string, unknown> || {}

  return (
    <div className="space-y-4">
      <p className="text-zinc-400 text-sm">Descoberta de criadores de conteúdo via <code className="text-zinc-300">facebook_creator_marketplace_discovery</code>.</p>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Search className="w-4 h-4 text-cyan-400" /> Informações do Criador</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-zinc-800 rounded-lg p-3">
            <p className="text-zinc-500 text-xs">Nome</p>
            <p className="text-white text-sm font-medium">{info.name as string || 'N/A'}</p>
          </div>
          <div className="bg-zinc-800 rounded-lg p-3">
            <p className="text-zinc-500 text-xs">Categoria</p>
            <p className="text-white text-sm font-medium">{info.category as string || 'N/A'}</p>
          </div>
          <div className="bg-zinc-800 rounded-lg p-3">
            <p className="text-zinc-500 text-xs">Seguidores</p>
            <p className="text-white text-sm font-medium">{((info.fan_count as number) || 0).toLocaleString()}</p>
          </div>
          <div className="bg-zinc-800 rounded-lg p-3">
            <p className="text-zinc-500 text-xs">Verificação</p>
            <p className="text-white text-sm font-medium">{info.verification_status as string || 'N/A'}</p>
          </div>
          <div className="bg-zinc-800 rounded-lg p-3 col-span-2">
            <p className="text-zinc-500 text-xs">Elegível para Branded Content</p>
            <p className={`text-sm font-medium ${info.is_eligible_for_branded_content ? 'text-emerald-400' : 'text-amber-400'}`}>
              {info.is_eligible_for_branded_content ? 'Sim ✓' : 'Não / Não verificado'}
            </p>
          </div>
        </div>
      </div>

      {data.error && <DebugPanel title="API Error" data={data.error} />}
      <DebugPanel title="Raw API Response" data={data} />
    </div>
  )
}

// ── Tab: Asset User Profile (Business Asset User Profile Access) ──

function AssetProfileTab({ pageId }: { pageId: string }) {
  const [data, setData] = useState<{ page: unknown; business_users: Record<string, unknown>[]; business_users_error?: unknown } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.get<{ page: unknown; business_users: Record<string, unknown>[]; business_users_error?: unknown }>(`/page-manager/${pageId}/asset-user-profile`)
      .then(d => setData(d))
      .finally(() => setLoading(false))
  }, [pageId])

  if (loading) return <Spinner />
  if (!data) return <Empty />

  return (
    <div className="space-y-4">
      <p className="text-zinc-400 text-sm">Perfil de usuários de ativos comerciais via <code className="text-zinc-300">Business Asset User Profile Access</code>.</p>

      <div className="space-y-2">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Usuários do Business ({data.business_users.length})</h3>
        {data.business_users.map((u, i) => (
          <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center text-sm font-bold text-zinc-400">
              {(u.name as string)?.[0] || '?'}
            </div>
            <div>
              <p className="text-white text-sm font-medium">{u.name as string}</p>
              <p className="text-zinc-500 text-xs">{u.email as string || 'Sem email'} • {u.role as string}</p>
            </div>
          </div>
        ))}
        {data.business_users.length === 0 && <Empty message="Nenhum usuário encontrado (página pode não estar em um Business Manager)." />}
      </div>

      {data.business_users_error && <DebugPanel title="API Error" data={data.business_users_error} />}
      <DebugPanel title="Raw API Response" data={data} />
    </div>
  )
}

// ── Demographics Test (pages_user_gender/locale/timezone) ──

function DemographicsTest({ pageId, psid }: { pageId: string; psid: string }) {
  const [data, setData] = useState<{ user: Record<string, unknown>; success: boolean; error?: unknown } | null>(null)
  const [loading, setLoading] = useState(false)

  const test = () => {
    setLoading(true)
    setData(null)
    api.get<{ user: Record<string, unknown>; success: boolean; error?: unknown }>(`/page-manager/${pageId}/user-demographics?psid=${psid}`)
      .then(d => setData(d))
      .finally(() => setLoading(false))
  }

  return (
    <div className="border-t border-zinc-800 pt-3 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <h4 className="text-zinc-400 text-xs font-medium">Dados Demográficos (pages_user_gender + pages_user_locale + pages_user_timezone)</h4>
        <Button onClick={test} disabled={loading} size="sm" variant="outline" className="gap-1 h-7 text-xs">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
          Testar API
        </Button>
      </div>
      {data && (
        <div className="space-y-2">
          {data.success && data.user ? (
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-zinc-800 rounded-lg p-2 text-center">
                <p className="text-zinc-500 text-xs">Gênero</p>
                <p className="text-white text-sm font-medium">{data.user.gender as string || 'N/A'}</p>
              </div>
              <div className="bg-zinc-800 rounded-lg p-2 text-center">
                <p className="text-zinc-500 text-xs">Idioma</p>
                <p className="text-white text-sm font-medium">{data.user.locale as string || 'N/A'}</p>
              </div>
              <div className="bg-zinc-800 rounded-lg p-2 text-center">
                <p className="text-zinc-500 text-xs">Fuso Horário</p>
                <p className="text-white text-sm font-medium">{data.user.timezone != null ? `UTC${data.user.timezone as number >= 0 ? '+' : ''}${data.user.timezone}` : 'N/A'}</p>
              </div>
            </div>
          ) : null}
          {data.error && <DebugPanel title="API Error" data={data.error} />}
          <DebugPanel title="Raw API Response" data={data} />
        </div>
      )}
    </div>
  )
}

// ── Tab: Instagram (instagram_basic + instagram_manage_messages) ──

function InstagramTab({ pageId }: { pageId: string }) {
  const [profile, setProfile] = useState<Record<string, unknown> | null>(null)
  const [media, setMedia] = useState<Record<string, unknown>[]>([])
  const [conversations, setConversations] = useState<Record<string, unknown>[]>([])
  const [loading, setLoading] = useState(true)
  const [rawProfile, setRawProfile] = useState<unknown>(null)
  const [rawConvos, setRawConvos] = useState<unknown>(null)
  const [msgRecipient, setMsgRecipient] = useState('')
  const [msgText, setMsgText] = useState('')
  const [msgResult, setMsgResult] = useState<{ success: boolean; text: string; data?: unknown } | null>(null)
  const [sending, setSending] = useState(false)
  const [convosLoading, setConvosLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    api.get<{ profile: Record<string, unknown>; media: Record<string, unknown>[]; ig_id: string | null; error?: string }>(`/page-manager/${pageId}/instagram-profile`)
      .then(d => { setProfile(d.profile || null); setMedia(d.media || []); setRawProfile(d) })
      .finally(() => setLoading(false))
  }, [pageId])

  const loadConversations = () => {
    setConvosLoading(true)
    api.get<{ conversations: Record<string, unknown>[]; error?: unknown }>(`/page-manager/${pageId}/instagram-conversations`)
      .then(d => { setConversations(d.conversations); setRawConvos(d) })
      .finally(() => setConvosLoading(false))
  }

  const sendIgMessage = async () => {
    if (!msgRecipient || !msgText.trim()) return
    setSending(true); setMsgResult(null)
    try {
      const res = await api.post<{ success: boolean; response: unknown }>(`/page-manager/${pageId}/instagram-send-message`, {
        recipient_id: msgRecipient, message: msgText.trim(),
      })
      setMsgResult({ success: res.success, text: res.success ? 'DM enviada!' : 'Erro', data: res.response })
      if (res.success) setMsgText('')
    } catch (e) { setMsgResult({ success: false, text: (e as Error).message }) }
    finally { setSending(false) }
  }

  if (loading) return <Spinner />

  return (
    <div className="space-y-6">
      {/* instagram_basic — Profile */}
      <div>
        <PermissionBadge permission="instagram_basic" />
        <div className="mt-3 space-y-3">
          {profile ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <div className="flex items-center gap-4">
                {profile.profile_picture_url && <img src={profile.profile_picture_url as string} className="w-16 h-16 rounded-full object-cover" />}
                <div>
                  <h3 className="text-white font-bold text-lg">@{profile.username as string}</h3>
                  <p className="text-zinc-400 text-sm">{profile.biography as string}</p>
                  <div className="flex gap-4 mt-2 text-xs text-zinc-500">
                    <span><strong className="text-white">{((profile.followers_count as number) || 0).toLocaleString()}</strong> seguidores</span>
                    <span><strong className="text-white">{((profile.follows_count as number) || 0).toLocaleString()}</strong> seguindo</span>
                    <span><strong className="text-white">{((profile.media_count as number) || 0).toLocaleString()}</strong> posts</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Empty message="Nenhuma conta Instagram Business vinculada a esta página." />
          )}

          {/* Media grid */}
          {media.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {media.map((m, i) => (
                <div key={i} className="aspect-square bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden relative group">
                  <img src={(m.media_url || m.thumbnail_url) as string} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 text-xs text-white">
                    <span>❤ {(m.like_count as number || 0)}</span>
                    <span>💬 {(m.comments_count as number || 0)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DebugPanel title="instagram_basic — Raw Response" data={rawProfile} />
      </div>

      {/* instagram_manage_messages — DMs */}
      <div>
        <PermissionBadge permission="instagram_manage_messages" />
        <div className="mt-3 bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4" /> DMs do Instagram</h3>
            <Button onClick={loadConversations} disabled={convosLoading} size="sm" variant="outline" className="gap-1 h-7 text-xs">
              {convosLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              Carregar Conversas
            </Button>
          </div>

          {conversations.length > 0 && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {conversations.map((conv, i) => {
                const participants = (conv.participants as { data?: Array<{ id?: string; username?: string }> })?.data || []
                return (
                  <button key={i} onClick={() => participants[0]?.id && setMsgRecipient(participants[0].id)}
                    className={`w-full text-left p-2 rounded-lg border text-sm ${msgRecipient === participants[0]?.id ? 'bg-blue-500/10 border-blue-500/30' : 'bg-zinc-800 border-zinc-700 hover:border-zinc-600'}`}>
                    <span className="text-white">{participants.map(p => p.username || p.id).join(', ')}</span>
                    <span className="text-zinc-600 text-xs ml-2">{conv.updated_time ? new Date(conv.updated_time as string).toLocaleDateString('pt-BR') : ''}</span>
                  </button>
                )
              })}
            </div>
          )}

          {/* Send IG DM */}
          <div className="border-t border-zinc-800 pt-3 space-y-2">
            <input value={msgRecipient} onChange={e => setMsgRecipient(e.target.value)} placeholder="IGSID do destinatário..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm placeholder:text-zinc-600" />
            <textarea value={msgText} onChange={e => setMsgText(e.target.value)} placeholder="Mensagem..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm resize-none h-16 placeholder:text-zinc-600" />
            <Button onClick={sendIgMessage} disabled={!msgRecipient || !msgText.trim() || sending} size="sm" className="gap-2">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Enviar DM (instagram_manage_messages)
            </Button>
            {msgResult && (
              <div className={`p-3 rounded-lg text-sm ${msgResult.success ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'}`}>
                <p>{msgResult.text}</p>
                {msgResult.data && <pre className="text-xs mt-1 opacity-70 overflow-x-auto">{JSON.stringify(msgResult.data, null, 2)}</pre>}
              </div>
            )}
          </div>
        </div>
        <DebugPanel title="instagram_manage_messages — Raw Response" data={rawConvos} />
      </div>
    </div>
  )
}

// ── Tab: Anúncios (ads_management) ──

function AdsTab({ pageId }: { pageId: string }) {
  const [data, setData] = useState<{ ad_accounts: Record<string, unknown>[]; campaigns: Record<string, unknown>[]; error?: unknown } | null>(null)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    api.get<{ ad_accounts: Record<string, unknown>[]; campaigns: Record<string, unknown>[]; error?: unknown }>(`/page-manager/${pageId}/ads-management`)
      .then(d => setData(d))
      .finally(() => setLoading(false))
  }

  return (
    <div className="space-y-4">
      <p className="text-zinc-400 text-sm">Contas de anúncio e campanhas via <code className="text-zinc-300">ads_management</code>.</p>

      <Button onClick={load} disabled={loading} size="sm" className="gap-2">
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
        Testar API — GET /me/adaccounts
      </Button>

      {data && (
        <div className="space-y-4">
          {data.error && <DebugPanel title="API Error" data={data.error} />}

          {/* Ad Accounts */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-2">Contas de Anúncio ({data.ad_accounts.length})</h3>
            <div className="space-y-2">
              {data.ad_accounts.map((acc, i) => {
                const statusMap: Record<number, string> = { 1: 'Ativa', 2: 'Desativada', 3: 'Não confirmada', 7: 'Pendente', 9: 'Em review', 101: 'Fechada' }
                return (
                  <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm font-medium">{acc.name as string || acc.id as string}</p>
                        <p className="text-zinc-600 text-xs">ID: {acc.account_id as string} • {acc.currency as string} • {acc.timezone_name as string}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        acc.account_status === 1 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'
                      }`}>{statusMap[acc.account_status as number] || acc.account_status}</span>
                    </div>
                    <div className="flex gap-4 mt-2 text-xs text-zinc-500">
                      <span>Gasto: <strong className="text-white">{((Number(acc.amount_spent) || 0) / 100).toFixed(2)}</strong></span>
                      <span>Saldo: <strong className="text-white">{((Number(acc.balance) || 0) / 100).toFixed(2)}</strong></span>
                    </div>
                  </div>
                )
              })}
              {data.ad_accounts.length === 0 && <Empty message="Nenhuma conta de anúncio encontrada." />}
            </div>
          </div>

          {/* Campaigns */}
          {data.campaigns.length > 0 && (
            <div>
              <h3 className="text-white font-semibold text-sm mb-2">Campanhas ({data.campaigns.length})</h3>
              <div className="space-y-2">
                {data.campaigns.map((c, i) => (
                  <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-medium">{c.name as string}</p>
                      <p className="text-zinc-600 text-xs">Objetivo: {c.objective as string}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-300' :
                      c.status === 'PAUSED' ? 'bg-amber-500/20 text-amber-300' :
                      'bg-zinc-700 text-zinc-400'
                    }`}>{c.status as string}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DebugPanel title="Raw API Response" data={data} />
        </div>
      )}
    </div>
  )
}

// ── Tab: Marketing Messages (marketing_messages_messenger + paid_marketing_messages) ──

function MarketingTab({ pageId }: { pageId: string }) {
  const [data, setData] = useState<{ messaging_features: Record<string, unknown>[]; page_info: unknown; features_error?: unknown; page_error?: unknown } | null>(null)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    api.get<{ messaging_features: Record<string, unknown>[]; page_info: unknown; features_error?: unknown; page_error?: unknown }>(`/page-manager/${pageId}/marketing-messages`)
      .then(d => setData(d))
      .finally(() => setLoading(false))
  }

  return (
    <div className="space-y-4">
      <p className="text-zinc-400 text-sm">Mensagens de marketing pagas no Messenger via <code className="text-zinc-300">marketing_messages_messenger</code> + <code className="text-zinc-300">paid_marketing_messages</code>.</p>

      <Button onClick={load} disabled={loading} size="sm" className="gap-2">
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
        Testar API — Messaging Features
      </Button>

      {data && (
        <div className="space-y-4">
          {/* Messaging Features */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-amber-400" /> Messaging Features</h3>
            {data.messaging_features.length > 0 ? (
              <div className="space-y-2">
                {data.messaging_features.map((f, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-300">{f.feature as string}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      f.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' :
                      f.status === 'pending' ? 'bg-amber-500/20 text-amber-300' :
                      'bg-zinc-700 text-zinc-400'
                    }`}>{f.status as string}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 text-sm">Nenhuma feature de marketing encontrada.</p>
            )}
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <h3 className="text-white font-semibold text-sm mb-2">Informações da Página</h3>
            <p className="text-zinc-400 text-sm">
              Estas permissões permitem criar, gerenciar e enviar mensagens de marketing pagas no Messenger, e visualizar o desempenho das campanhas.
            </p>
          </div>

          {data.features_error && <DebugPanel title="Features Error" data={data.features_error} />}
          {data.page_error && <DebugPanel title="Page Error" data={data.page_error} />}
          <DebugPanel title="Raw API Response" data={data} />
        </div>
      )}
    </div>
  )
}

// ── Shared components ──

function StatCard({ label, value }: { label: string; value?: number | null }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
      <p className="text-2xl font-bold text-white">{(value ?? 0).toLocaleString()}</p>
      <p className="text-zinc-500 text-xs mt-1">{label}</p>
    </div>
  )
}

function Spinner() {
  return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
}

function Empty({ message = 'Nenhum dado encontrado.' }: { message?: string }) {
  return <p className="text-zinc-500 text-sm text-center py-8">{message}</p>
}
